import { useEffect, useState } from 'react';
import { Loader2, Mic } from 'lucide-react';
import { preferServerVoiceStt } from '../../utils/webSpeechRecognition';

type VoiceCaptureModalProps = {
  open: boolean;
  onClose: () => void;
  transcript: string;
  isRecording: boolean;
  recordingTranscribing: boolean;
  speechInterimActive?: boolean;
  onStop: (transcriptAtClick: string) => void;
  sending: boolean;
  embedInPanel?: boolean;
};

/**
 * Minimal voice sheet: status + Cancel / Done. Optional one-line live transcript when using browser STT
 * (`!preferServerVoiceStt()`); hidden for Whisper / mobile-default path.
 */
export function VoiceCaptureModal({
  open,
  onClose,
  transcript,
  isRecording,
  recordingTranscribing,
  onStop,
  sending,
  embedInPanel = false,
}: VoiceCaptureModalProps) {
  const [backdropDismissOk, setBackdropDismissOk] = useState(false);

  useEffect(() => {
    if (!open) {
      setBackdropDismissOk(false);
      return;
    }
    setBackdropDismissOk(false);
    const id = window.setTimeout(() => setBackdropDismissOk(true), 450);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const layer = embedInPanel ? 'absolute' : 'fixed';
  const showStop = isRecording && !recordingTranscribing;
  const stopSendEnabled = Boolean(showStop && !sending);

  return (
    <div className={`${layer} inset-0 z-modal-content flex items-end justify-center sm:items-center sm:p-3`}>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label="Dismiss"
        onClick={() => {
          if (!backdropDismissOk) return;
          onClose();
        }}
      />
      <div
        className="relative z-10 w-full max-w-sm rounded-t-2xl border border-subtle/80 border-b-0 bg-surface px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 shadow-2xl sm:rounded-2xl sm:border-b sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-modal-title"
      >
        <h2 id="voice-modal-title" className="sr-only">
          Voice input
        </h2>

        <div className="mb-5 flex items-center justify-center gap-2 text-sm text-th-secondary">
          {recordingTranscribing ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-brand" aria-hidden />
          ) : (
            <Mic className="h-5 w-5 shrink-0 text-brand" aria-hidden />
          )}
          <span aria-live="polite">
            {recordingTranscribing ? 'Transcribing…' : isRecording ? 'Listening…' : 'Ready'}
          </span>
        </div>

        {!preferServerVoiceStt() ? (
          <p
            className="mb-4 line-clamp-2 min-h-[2.25rem] text-center text-xs leading-snug text-th-tertiary"
            aria-live="polite"
          >
            {transcript.trim() ? transcript.trim() : isRecording ? '…' : '\u00A0'}
          </p>
        ) : null}

        <div className="flex gap-2">
          <button
            type="button"
            className="min-h-12 flex-1 rounded-xl border border-subtle/80 bg-elevated/80 py-3 text-sm font-medium text-th-primary transition hover:bg-overlay disabled:opacity-50"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          {showStop ? (
            <button
              type="button"
              className="min-h-12 flex-[1.15] rounded-xl bg-brand py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:pointer-events-none disabled:opacity-40"
              onClick={() => {
                if (!stopSendEnabled) return;
                onStop(transcript);
              }}
              disabled={!stopSendEnabled}
              aria-label="Finish and send"
            >
              Done
            </button>
          ) : recordingTranscribing ? (
            <div className="flex min-h-12 flex-[1.15] items-center justify-center gap-2 rounded-xl bg-subtle/40 px-3 text-sm text-th-tertiary">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
              <span>Wait…</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
